import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { User, UserManager } from 'oidc-client-ts';
import { oidcSettings } from './oidc-config';
import { authApi } from '@/lib/api/auth.api';
import { registerTokenAccessor, writeActiveOrganizationId } from '@/lib/api/client';
import {
  AUTHENTICATION_REQUIRED_EVENT,
  isAuthenticationRequiredMessage,
  type AuthenticationRequiredMessage,
} from '@nwm-oss/auth-react/authentication-required';

interface AuthState {
  readonly user: User | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

interface AuthContextValue extends AuthState {
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  handleCallback: () => Promise<string | null>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Lazily construct the manager exactly once. `useRef(new UserManager(...))` would
  // re-evaluate the constructor on every render; oidc-client-ts starts silent-renew
  // timers and a session-monitor iframe in its constructor, so a re-constructed and
  // discarded instance on every render leaks live polling/renewal timers.
  const [manager] = useState<UserManager>(() => new UserManager(oidcSettings));
  const [isOrganizationBootstrapPending, setIsOrganizationBootstrapPending] = useState(false);
  const authenticationRedirectStarted = React.useRef(false);

  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const setUser = (user: User | null) => {
      setState({
        user,
        isAuthenticated: !!user && !user.expired,
        isLoading: false,
        error: null,
      });
    };

    manager
      .getUser()
      .then(setUser)
      .catch((err: unknown) => {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    const handleUserLoaded = (u: User) => setUser(u);
    const handleUserUnloaded = () => setUser(null);
    const handleAccessTokenExpired = () => {
      if (authenticationRedirectStarted.current) return;
      authenticationRedirectStarted.current = true;
      writeActiveOrganizationId(null);
      setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
      void manager.removeUser().finally(() =>
        manager.signinRedirect({
          state: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` },
        }),
      );
    };
    const handleSilentRenewErr = (e: Error) => {
      const message = e.message.toLowerCase();
      const isDeadSession =
        message.includes('invalid_grant') ||
        message.includes('invalid_token') ||
        message.includes('session not active') ||
        message.includes('login_required');

      if (isDeadSession) {
        void manager.removeUser();
        writeActiveOrganizationId(null);
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: e,
        });
        return;
      }

      setState((prev) => ({ ...prev, error: e }));
    };

    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addUserUnloaded(handleUserUnloaded);
    manager.events.addAccessTokenExpired(handleAccessTokenExpired);
    manager.events.addSilentRenewError(handleSilentRenewErr);

    return () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeUserUnloaded(handleUserUnloaded);
      manager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      manager.events.removeSilentRenewError(handleSilentRenewErr);
    };
  }, [manager]);

  useEffect(() => {
    const redirectToLogin = (returnTo?: string) => {
      if (authenticationRedirectStarted.current) return;
      authenticationRedirectStarted.current = true;
      writeActiveOrganizationId(null);
      setState({ user: null, isAuthenticated: false, isLoading: false, error: null });
      void manager.removeUser().finally(() =>
        manager.signinRedirect({
          state: {
            returnTo: returnTo ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        }),
      );
    };
    const handleLocal = (event: Event) =>
      redirectToLogin((event as CustomEvent<AuthenticationRequiredMessage>).detail?.returnTo);
    const handleMessage = (event: MessageEvent) => {
      const sentByHostedWorkspace = Array.from(document.querySelectorAll('iframe')).some(
        (frame) => frame.contentWindow === event.source,
      );
      if (sentByHostedWorkspace && isAuthenticationRequiredMessage(event.data)) {
        // The child route belongs to its iframe, not to the shell router. Return
        // to the shell URL that was active when authentication was lost.
        redirectToLogin();
      }
    };
    window.addEventListener(AUTHENTICATION_REQUIRED_EVENT, handleLocal);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener(AUTHENTICATION_REQUIRED_EVENT, handleLocal);
      window.removeEventListener('message', handleMessage);
    };
  }, [manager]);

  const login = useCallback(
    async (returnTo?: string) => {
      const state = returnTo ? { returnTo } : undefined;
      await manager.signinRedirect({ state });
    },
    [manager],
  );

  const logout = useCallback(async () => {
    writeActiveOrganizationId(null);
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    await manager.removeUser();
    await manager.signoutRedirect();
  }, [manager]);

  const handleCallback = useCallback(async (): Promise<string | null> => {
    const user = await manager.signinRedirectCallback();
    setState({
      user,
      isAuthenticated: !user.expired,
      isLoading: false,
      error: null,
    });

    const userState = user.state as { returnTo?: string } | string | null | undefined;
    if (typeof userState === 'string') {
      return userState;
    }
    return userState?.returnTo ?? null;
  }, [manager]);

  const getAccessToken = useCallback((): string | null => {
    return state.user?.access_token ?? null;
  }, [state.user]);

  useEffect(() => {
    registerTokenAccessor(() => state.user?.access_token ?? null);
  }, [state.user]);

  useEffect(() => {
    if (!state.user || state.user.expired) {
      writeActiveOrganizationId(null);
      setIsOrganizationBootstrapPending(false);
      return;
    }

    let cancelled = false;
    setIsOrganizationBootstrapPending(true);

    void (async () => {
      try {
        const organizations = await authApi.organizations();
        if (cancelled) {
          return;
        }

        if (organizations.activeOrganizationId) {
          writeActiveOrganizationId(organizations.activeOrganizationId);
          return;
        }

        const fallbackOrganizationId =
          organizations.organizations.find((organization) => organization.isPreferred)?.organizationId ??
          organizations.organizations[0]?.organizationId ??
          null;

        if (!fallbackOrganizationId) {
          writeActiveOrganizationId(null);
          return;
        }

        const selection = await authApi.setActiveOrganization(fallbackOrganizationId);
        if (cancelled) {
          return;
        }
        writeActiveOrganizationId(selection.activeOrganizationId);
      } catch (error) {
        if (!cancelled) {
          setState((current) => ({
            ...current,
            error: error instanceof Error ? error : new Error(String(error)),
          }));
        }
      } finally {
        if (!cancelled) {
          setIsOrganizationBootstrapPending(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [state.user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      // Keep protected workspaces mounted while a silent token renew refreshes
      // organization context. Unmounting here discards in-progress workspace forms.
      isLoading: state.isLoading,
      login,
      logout,
      handleCallback,
      getAccessToken,
    }),
    [state, isOrganizationBootstrapPending, login, logout, handleCallback, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
